import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, Copy, ShieldCheck } from "lucide-react";

const OTP_TTL = 600; // 10 minutes

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("email"); // "email" | "otp"
  const [otp, setOtp] = useState(["", "", "", "", ""]);
  const [devOtp, setDevOtp] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);
  const inputRefs = useRef([]);

  const startCountdown = (seconds = OTP_TTL) => {
    setCountdown(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const sendOtp = async () => {
    if (!email.trim()) { toast.error("Enter your admin email"); return; }
    setBusy(true);
    try {
      const r = await api.adminSendOtp(email.trim().toLowerCase());
      setDevOtp(r.dev_otp || null); // only present in mock/dev mode
      setStep("otp");
      startCountdown(r.expires_in_seconds || OTP_TTL);
      toast.success(r.channel === "email" ? `OTP sent to ${email.trim()}` : "OTP generated (demo mode)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    } finally { setBusy(false); }
  };

  const handleOtpChange = (i, val) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const copy = [...otp];
    copy[i] = digit;
    setOtp(copy);
    if (digit && i < 4) inputRefs.current[i + 1]?.focus();
    if (!digit && i > 0) inputRefs.current[i - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 5);
    if (!text) return;
    const copy = text.split("").concat(Array(5).fill("")).slice(0, 5);
    setOtp(copy);
    inputRefs.current[Math.min(text.length, 4)]?.focus();
    e.preventDefault();
  };

  const verify = async () => {
    const code = otp.join("");
    if (code.length < 5) { toast.error("Enter the full 5-digit OTP"); return; }
    setBusy(true);
    try {
      const r = await api.adminVerifyOtp(email.trim().toLowerCase(), code);
      localStorage.setItem("proctorly_admin_token", r.token);
      localStorage.setItem("proctorly_admin_email", r.email);
      toast.success(`Welcome, ${r.email}`);
      navigate("/admin");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid OTP");
    } finally { setBusy(false); }
  };

  const copyOtp = () => {
    if (!devOtp) return;
    navigator.clipboard.writeText(devOtp);
    setOtp(devOtp.split("").concat(Array(5).fill("")).slice(0, 5));
    toast.success("OTP copied & auto-filled");
  };

  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="min-h-screen text-white" style={{ background: "var(--kl-navy-deep)" }}>
      <Header />
      <div className="max-w-lg mx-auto px-6 py-20 relative">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(230,57,70,0.6), transparent 60%)" }} />

        <div className="relative z-10">
          <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-2">
            <span className="kl-accent-line mr-3" />ADMIN · SECURE LOGIN
          </div>
          <h1 className="font-display text-4xl md:text-5xl uppercase leading-[0.9] tracking-tight mb-2">
            Admin <span className="text-[color:var(--kl-red)]">Access</span>
          </h1>
          <p className="text-white/50 text-sm mb-8">
            Verify your email to manage tournaments and view analytics.
          </p>

          <div className="rounded-md border border-white/10 p-6 md:p-8" style={{ background: "rgba(255,255,255,0.02)" }}>
            {step === "email" && (
              <>
                <label className="form-label">Admin Email <span className="req">*</span></label>
                <input
                  className="input-dark mb-5"
                  type="email"
                  placeholder="admin@kibalabs.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendOtp()}
                  autoFocus
                />
                <button onClick={sendOtp} disabled={busy} className="btn-submit w-full justify-center">
                  {busy ? "Sending…" : "Send OTP"} <ArrowUpRight className="w-4 h-4" />
                </button>
              </>
            )}

            {step === "otp" && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-4 h-4 text-[color:var(--kl-red)]" />
                  <span className="font-mono-kl text-xs uppercase tracking-widest text-white/60">
                    {devOtp ? "Demo OTP generated" : `OTP emailed to ${email}`}
                  </span>
                </div>

                {devOtp && (
                  <div className="my-4 rounded-md border border-[color:var(--kl-red)]/40 p-4 flex items-center justify-between gap-3"
                    style={{ background: "rgba(230,57,70,0.08)" }}>
                    <div>
                      <div className="font-mono-kl text-[10px] uppercase tracking-widest text-[color:var(--kl-red)] mb-1">Your OTP</div>
                      <div className="font-display text-4xl tracking-[0.2em] text-white">{devOtp}</div>
                      <div className="font-mono-kl text-[10px] text-white/40 mt-1">
                        {countdown > 0 ? `Expires in ${fmtTime(countdown)}` : "Expired — request a new OTP"}
                      </div>
                    </div>
                    <button onClick={copyOtp} className="btn-ghost-dark flex-shrink-0" title="Copy & auto-fill">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <label className="form-label mt-2">Enter OTP <span className="req">*</span></label>
                <div className="flex gap-2 mt-2 mb-5" onPaste={handleOtpPaste}>
                  {otp.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputRefs.current[i] = el)}
                      className="input-dark text-center text-2xl font-display w-12 h-14 p-0"
                      maxLength={1}
                      value={d}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && !otp[i] && i > 0) inputRefs.current[i - 1]?.focus();
                        if (e.key === "Enter") verify();
                      }}
                    />
                  ))}
                </div>

                <button onClick={verify} disabled={busy || countdown === 0} className="btn-submit w-full justify-center">
                  {busy ? "Verifying…" : "Verify & Enter"} <ArrowUpRight className="w-4 h-4" />
                </button>

                <button
                  onClick={() => { setStep("email"); setOtp(["", "", "", "", ""]); setDevOtp(null); }}
                  className="mt-3 w-full text-center text-white/40 hover:text-white text-xs font-mono-kl uppercase tracking-widest"
                >
                  ← Use a different email
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
